import { HttpError, type HttpPort } from "~/lib/http";

export interface AuthUser {
  id: string;
  role: "teacher" | "student";
  firstName: string;
  lastName: string;
  email?: string;
  schoolRole?: "owner" | "teacher" | null;
  schoolName?: string | null;
  classId?: string;
  className?: string;
}

export interface AuthResult {
  token: string;
  user: AuthUser;
}

interface AuthResponse {
  csrfToken: string;
  user: AuthUser;
}

export interface AuthSession extends AuthResult {
  currentClass?: { id: string; name?: string };
}

export interface StoragePort {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
}

export type LoginAudience = "student" | "teacher" | "principal";
export interface SavedTeacher { email: string }
export interface SavedStudent { code: string; name: string }

export function createAuthModule(http: HttpPort, storage: StoragePort) {
  const clearSession = () => {
    storage.remove("reflexo_csrf");
    storage.remove("reflexo_token");
    storage.remove("reflexo_user");
    storage.remove("reflexo_class");
  };
  const persistSession = (response: AuthResponse): AuthResult => {
    const result = { token: response.csrfToken, user: response.user };
    storage.set("reflexo_csrf", result.token);
    storage.remove("reflexo_token");
    storage.set("reflexo_user", JSON.stringify(result.user));
    if (result.user.role === "student" && result.user.classId) {
      storage.set("reflexo_class", JSON.stringify({ id: result.user.classId, name: result.user.className }));
    }
    return result;
  };
  const read = <T>(key: string): T | undefined => {
    try {
      const value = storage.get(key);
      if (!value) return undefined;
      const parsed = JSON.parse(value) as Record<string, string>;
      const { pw: _legacyPassword, password: _password, ...savedValues } = parsed;
      return savedValues as T;
    } catch {
      return undefined;
    }
  };
  const remember = (key: string, value: Record<string, string> | undefined) => {
    if (!value) return storage.remove(key);
    storage.set(key, JSON.stringify(value));
  };

  return {
    clearLocalSession: clearSession,
    current(): AuthSession | undefined {
      const token = storage.get("reflexo_csrf");
      const user = read<AuthUser>("reflexo_user");
      if (!token || !user || (user.role !== "teacher" && user.role !== "student")) return undefined;
      return { token, user, currentClass: read<AuthSession["currentClass"]>("reflexo_class") };
    },
    async restore(): Promise<AuthSession | undefined> {
      const current = this.current();
      if (!current) return undefined;
      try {
        const response = await http.request<{ user: AuthUser }>({ path: "/auth/me" });
        const result = persistSession({ csrfToken: current.token, user: response.user });
        return { ...result, currentClass: read<AuthSession["currentClass"]>("reflexo_class") };
      } catch (cause) {
        if (cause instanceof HttpError && cause.status !== 401) throw cause;
        clearSession();
        return undefined;
      }
    },
    selectClass(currentClass: AuthSession["currentClass"] | undefined) {
      if (!currentClass) {
        storage.remove("reflexo_class");
        return;
      }
      storage.set("reflexo_class", JSON.stringify(currentClass));
    },
    updateUser(patch: Partial<AuthUser>): AuthUser | undefined {
      const session = this.current();
      if (!session) return undefined;
      const user = { ...session.user, ...patch };
      storage.set("reflexo_user", JSON.stringify(user));
      return user;
    },
    loginAudience(): LoginAudience {
      const value = storage.get("reflexo_login_audience");
      return value === "student" || value === "principal" ? value : "teacher";
    },
    rememberLoginAudience(audience: LoginAudience) {
      storage.set("reflexo_login_audience", audience);
    },
    savedTeacher: () => read<SavedTeacher>("reflexo_saved_teacher"),
    savedStudent: () => read<SavedStudent>("reflexo_saved_student"),
    async loginTeacher(input: SavedTeacher & { password: string; remember: boolean }) {
      const response = await http.request<AuthResponse>({ path: "/auth/login", method: "POST", body: { email: input.email, password: input.password, remember: input.remember } });
      remember("reflexo_saved_teacher", input.remember ? { email: input.email } : undefined);
      return persistSession(response);
    },
    // Nach Freigabe und Vertragsabschluss legt der Server die Schule an und meldet die Schulleitung direkt an.
    async completeSchoolRegistration(input: { token: string; password: string }) {
      const response = await http.request<AuthResponse>({ path: "/schools/register/complete", method: "POST", body: { token: input.token, password: input.password, accept: true } });
      return persistSession(response);
    },
    // Eingeladene Lehrkraft: Der Server legt das Konto an und meldet direkt an.
    async acceptInvitation(input: { token: string; password: string; firstName: string; lastName: string }) {
      const response = await http.request<AuthResponse>({ path: "/invitations/accept", method: "POST", body: input });
      return persistSession(response);
    },
    async loginStudent(input: SavedStudent & { password: string; remember: boolean }) {
      const response = await http.request<AuthResponse>({ path: "/auth/student-login", method: "POST", body: { joinCode: input.code, firstName: input.name, password: input.password, remember: input.remember } });
      remember("reflexo_saved_student", input.remember ? { code: input.code, name: input.name } : undefined);
      return persistSession(response);
    },
    async logout() {
      const token = storage.get("reflexo_csrf");
      try {
        if (token) await http.request({ path: "/auth/logout", method: "POST", token, body: {} });
      } finally {
        clearSession();
      }
    }
  };
}

export type AuthModule = ReturnType<typeof createAuthModule>;

export function createBrowserStorage(storage: Storage): StoragePort {
  return {
    get: (key) => storage.getItem(key),
    set: (key, value) => storage.setItem(key, value),
    remove: (key) => storage.removeItem(key)
  };
}
